import { Alert, Anchor, Badge, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import type { CpgMeasure } from './hooks'

export type CpgMeasureCitation = CpgMeasure['citations'][number]

/**
 * Guideline passages — a measure's citations, or a proposal cluster's suggested ones. Each
 * links to our own copy of the cited document VERSION with the passage highlighted
 * (`?doc=&unit=`), and on to the source's page.
 */
export function GuidelineBasis({
  citations,
  title = 'Guideline basis',
  empty = 'No guideline — this measure cites no passage in the guideline corpus.',
}: {
  citations: CpgMeasureCitation[]
  title?: string
  empty?: string
}) {
  return (
    <div className="mt-3">
      <Text size="sm" fw={600} tt="uppercase" c="dimmed" className="mb-1">
        {title}
      </Text>
      {citations.length === 0 ? (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
          {empty}
        </Alert>
      ) : (
        <Stack gap={6}>
          {citations.map((c) => (
            <div key={c.unitId} className="rounded border border-gray-200 px-3 py-2">
              <Group gap="xs" wrap="nowrap" align="baseline">
                <Badge variant="outline" color="dark" radius="sm" size="sm" className="shrink-0">
                  {c.gradeOriginal ? `Grade ${c.gradeOriginal}` : c.unitKind}
                </Badge>
                <Anchor
                  component={Link}
                  to={`/super/cpg/documents?doc=${c.documentId}&unit=${c.unitId}`}
                  size="sm"
                  className="min-w-0"
                >
                  {c.source.toUpperCase()} · {c.documentTitle} (
                  {c.publishedAt?.slice(0, 4) ?? c.version})
                </Anchor>
                <Anchor
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                  c="dimmed"
                  aria-label={`Open ${c.documentTitle} at the source`}
                  className="shrink-0"
                >
                  <IconExternalLink size={14} />
                </Anchor>
              </Group>
              {c.documentStatus !== 'active' && (
                <Text size="xs" c="orange" className="mt-1">
                  Cited version is {c.documentStatus} — review the measure against the current
                  guideline.
                </Text>
              )}
              <Text size="xs" c="dimmed" className="mt-1">
                {c.excerpt}
              </Text>
            </div>
          ))}
        </Stack>
      )}
    </div>
  )
}
